import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { Container } from '@johnlewispartnership/wtr-ingredients/dist/foundations/grid';
import { useMediaQuery } from 'react-responsive';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useDispatch, useSelector } from 'react-redux';
import { BREAKPOINTS } from 'constants/grid';
import TopNav from 'components/SiteHeaderNew/TopNav';
import HeaderLogo from 'components/HeaderLogo';
import { useScrolled } from 'hooks/use-scrolled';
import SearchForm from 'components/Search/SearchForm';
import SkipLink from 'components/SkipLink';
import { stickyHeaderOffset } from 'constants/siteHeader';
import { thirtyDaysInMilliseconds } from 'constants/cookies';
import { usePreloadedData } from 'contexts/PreloadedData';
import contentService from 'services/content';
import useMonetateReporter from 'hooks/use-monetate-reporter';
import { useSlotContext } from 'contexts/Slot';
import { deleteCookie, setCookie } from 'utils/cookie';
import SitePinBar from 'components/SitePinBar';
import AppBanner from 'components/AppBanner';
import { useMinSpend } from 'hooks/use-min-spend';
import { useHeaderHeight } from 'hooks/use-header-height';
import setClearValueAction from 'redux/modules/page/actions/set-clear-value';
import setMobileOverlayAction from 'redux/modules/page/actions/set-mobile-overlay';
import SlideOutNav from './SlideOutNav';
import styles from './SiteHeader.module.scss';
import DropdownNav from './LinksBar/DropdownNav';
import DropdownNavHidden from './LinksBar/DropdownNavHidden';
import LinksBar from './LinksBar';
import MenuLinksBar from './MenuLinksBar';
import MobileOverlay from '../SiteHeader/MobileOverlay';

const fetchContent = async (experienceFragmentKey, hasTokenSession) =>
  contentService.getContent(experienceFragmentKey, hasTokenSession);

const SiteHeader = () => {
  const { state: authState } = useAuthentication();
  const { showMinSpend } = useMinSpend();
  const isOverlayOpen = useSelector(state => state.page.isMobileOverlayOpen);
  const clearValue = useSelector(state => state.page.isValueClear);

  useMonetateReporter();

  const [isTablet, setIsTablet] = useState(false);

  const [isSeasonalMenuOpen, setIsSeasonalMenuOpen] = useState(false);

  const ref = useRef();
  useHeaderHeight(ref);
  const { scrolled } = useScrolled(stickyHeaderOffset);
  const placeholderStyle = useMemo(() => {
    const { current: el } = ref;
    if (!scrolled || !el) return { display: 'none' };
    return { height: el.getBoundingClientRect().height };
  }, [scrolled]);

  const dispatch = useDispatch();

  const setClearValue = useCallback(
    isValueClear => dispatch(setClearValueAction(isValueClear)),
    [dispatch],
  );

  const setMobileOverlay = useCallback(
    isOpen => dispatch(setMobileOverlayAction(isOpen)),
    [dispatch],
  );

  const toggleOverlay = useCallback(() => {
    setClearValue(true);
    setMobileOverlay(!isOverlayOpen);
  }, [isOverlayOpen, setClearValue, setMobileOverlay]);

  // DropdownNav is rendered at a different position depending on whether it
  // is sticky or not so that the tabbing order is correct alongside search form/header buttons
  const menu = (
    <div className={styles.menu} key="desktopMenu">
      <DropdownNav scrolled={scrolled} />
    </div>
  );

  const isTabletBreakpoint = useMediaQuery({
    minWidth: BREAKPOINTS.md,
    maxWidth: BREAKPOINTS.lg - 1,
  });
  const isXLDesktop = useMediaQuery({ minWidth: BREAKPOINTS.xl });
  const isDesktop = useMediaQuery({ minWidth: BREAKPOINTS.lg, maxWidth: BREAKPOINTS.xl - 1 });
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.md - 1 });
  const isXSMobile = useMediaQuery({ maxWidth: BREAKPOINTS.xs_sm - 1 });

  const viewport = useMemo(() => {
    if (isXSMobile) return 'xs-mobile';
    if (isMobile) return 'mobile';
    if (isTabletBreakpoint) return 'tablet';
    if (isDesktop) return 'desktop';
    if (isXLDesktop) return 'xl-desktop';
    return 'desktop';
  }, [isXSMobile, isMobile, isTabletBreakpoint, isDesktop, isXLDesktop]);

  useEffect(() => {
    if (authState.initialised) {
      if (authState.isLoggedIn) {
        // Set customerId cookie for server-side reporting and third-party integrations
        setCookie('customerId', authState.customerId, {
          secure: true,
          maxAge: thirtyDaysInMilliseconds,
        });
      } else {
        deleteCookie('customerId');

        if (authState.hasTokenSession) {
          // Server-side rendered the logged-in header but now we've done the auth flow
          // client-side and they're not actually logged in, so it'll re-render logged-out.
          // This is expected e.g. when token expires, but we log it so we can track/analyse.
          const errorToSendToApm = new Error('Re-rendering header from logged-in to logged-out');
          window?.apm?.captureError(
            Object.assign(errorToSendToApm, {
              event: 'header_flicker',
              authError: authState.error?.message,
            }),
          );
        }
      }
    }
  }, [
    authState.customerId,
    authState.error,
    authState.hasTokenSession,
    authState.initialised,
    authState.isLoggedIn,
  ]);

  useEffect(() => {
    setIsTablet(isTabletBreakpoint);
  }, [isTabletBreakpoint]);

  const { preloadedData, setPreloadedData } = usePreloadedData();

  useEffect(() => {
    if (
      typeof preloadedData?.middleMegaMenu === 'undefined' ||
      typeof preloadedData?.seasonalMenu === 'undefined'
    ) {
      const middleMegaMenuContent =
        typeof preloadedData?.middleMegaMenu === 'undefined'
          ? fetchContent('menu/middle-mega-menu/master', authState.hasTokenSession)
          : preloadedData.middleMegaMenu;

      const seasonalMenuContent =
        typeof preloadedData?.seasonalMenu === 'undefined'
          ? fetchContent(
              'menu/seasonal-menu/seasonal-menu-content/master',
              authState.hasTokenSession,
            )
          : preloadedData.seasonalMenu;

      // deepcode ignore PromiseNotCaughtGeneral - SeviceClients have built-in error handling and always resolves
      Promise.allSettled([middleMegaMenuContent, seasonalMenuContent]).then(
        ([middleMegaMenu, seasonalMenu]) => {
          setPreloadedData({
            middleMegaMenu: middleMegaMenu.value,
            seasonalMenu: seasonalMenu.value,
          });
        },
      );
    }
  }, [preloadedData, setPreloadedData, authState.hasTokenSession]);

  const shouldRenderLoggedInVersion =
    (authState.hasTokenSession && !authState.initialised) ||
    (authState.initialised && authState.isLoggedIn);

  const {
    slotContext: { loading: slotLoading, booked: slotBooked, details: slotDetails },
  } = useSlotContext();

  const toggleSeasonalMenu = useCallback(
    () => setIsSeasonalMenuOpen(!isSeasonalMenuOpen),
    [isSeasonalMenuOpen],
  );

  return (
    <>
      {/*
        This style tag avoids a flash of unstyled content.
        These styles are overwritten by the CSS file once loaded.
        See WPIP-72426.
      */}
      <style>
        {`
          .common-mfe-header {
            height: 50px;
            visibility: hidden;

            @media (min-width: 992px) {
              height: 159px;
            }
          }
        `}
      </style>
      <div className={`${styles.header} common-mfe-header`} data-testid="site-header">
        <SkipLink />
        <AppBanner />
        <TopNav />
        <div className={styles.desktop}>
          <DropdownNavHidden />
          {scrolled && <div data-testid="DesktopHeaderPlaceholder" style={placeholderStyle} />}
          <div
            className={classNames(styles.desktopHeader, {
              [styles.scrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
            })}
            data-testid="DesktopHeader"
            ref={ref}
          >
            <Container className={classNames(styles.container)}>
              <div
                className={classNames(styles.mobileMenu, { [styles.hasMinSpend]: showMinSpend })}
              >
                <SlideOutNav
                  isSlotBooked={slotBooked}
                  slotDetails={slotDetails}
                  slotLoading={slotLoading}
                  isLoggedIn={shouldRenderLoggedInVersion}
                />
              </div>
              <div
                className={classNames(styles.mobileLogo, { [styles.hasMinSpend]: showMinSpend })}
              >
                <HeaderLogo small staticRender={false} />
              </div>
              <div className={styles.logo}>
                <HeaderLogo staticRender={false} />
              </div>

              {scrolled && menu}

              <div
                className={classNames(styles.inner, {
                  [styles.isScrolled]: scrolled,
                  [styles.hasMinSpend]: showMinSpend,
                })}
                data-testid="inner"
              >
                {isMobile && (
                  <div
                    className={classNames(styles.links, {
                      [styles.hasMinSpend]: showMinSpend,
                    })}
                    data-testid="links"
                  >
                    <LinksBar
                      currentPageIsGroceriesHomePage={false}
                      viewport={viewport}
                      isLoggedIn={shouldRenderLoggedInVersion}
                      isSeasonalMenuOpen={isSeasonalMenuOpen}
                      onToggleSeasonalMenu={toggleSeasonalMenu}
                      scrolled={scrolled}
                      showContactAddressNotPresentNotification={false}
                      toggleOverlay={toggleOverlay}
                    />
                  </div>
                )}
                <div className={styles.search} data-testid="search">
                  <SearchForm placeholder={isTablet ? 'Search...' : 'Search groceries...'} />
                </div>

                {!isMobile && (
                  <div className={styles.links} data-testid="links">
                    <LinksBar
                      viewport={viewport}
                      showContactAddressNotPresentNotification={false}
                      currentPageIsGroceriesHomePage={false}
                      isLoggedIn={shouldRenderLoggedInVersion}
                      onToggleSeasonalMenu={toggleSeasonalMenu}
                      isSeasonalMenuOpen={isSeasonalMenuOpen}
                      scrolled={scrolled}
                    />
                  </div>
                )}
              </div>

              {!scrolled && menu}
              {(isDesktop || isXLDesktop) && !scrolled && (
                <div className={styles.links}>
                  <MenuLinksBar
                    onToggleSeasonalMenu={toggleSeasonalMenu}
                    isSeasonalMenuOpen={isSeasonalMenuOpen}
                  />
                </div>
              )}
            </Container>
          </div>
        </div>
        {isOverlayOpen && <MobileOverlay clearValue={clearValue} setClearValue={setClearValue} />}
        <SitePinBar />
      </div>
    </>
  );
};

export { SiteHeader };
